/* eslint-disable @typescript-eslint/no-explicit-any */

import { Component, ViewEncapsulation, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CompositeDataService } from 'src/app/shared/services/data-api/composite-data.service';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { takeUntil, filter, startWith, take } from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { AuthService } from 'src/app/service/auth.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { UserStateService } from 'src/app/shared/services/user-state.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { ConfigService } from 'src/app/service/config.service';
import { BreakpointService } from 'src/app/shared/services/breakpoints.service';
import { SentimentService } from 'src/app/shared/services/data-api/sentiment.service';
import { PriceDataService } from 'src/app/shared/services/quant-api/price-data.service';
import { PeergroupContext } from './peergroup-comparison.component';
import { scrollIntoViewIfNotVisible } from './chartmap-mosaic-shared';
import { UserScopedStorageService } from 'src/app/shared/services/user-scoped-storage.service';

/**
 * Company page container: hosts the mosaic sub-components (flag events,
 * sentiment events, score/alert events, peergroup comparison) and owns the
 * shared refresh gate, the column filter and the entity/price column.
 */
@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-chartmap-company',
  templateUrl: './chartmap-company.component.html',
  styleUrls: ['./chartmap-company.component.scss'],
})
export class ChartmapCompanyComponent implements OnDestroy {
  /**
   * columns to display
   */
  ifilter: any;

  /**
   * query parameters
   */
  qparam: any;

  /**
   * Gated copy of qparam — only reassigned when a refetch is due; drives the
   * mosaic sub-components' fetches.
   */
  mosaicParams: any;

  /** Set once the entity fetch succeeded; triggers the peergroup mosaic. */
  peergroupCtx: PeergroupContext | null = null;

  /**
   * company data info
   */
  entity: any;

  /**
   * treemap of the company
   */
  treemapsort: any;
  treemapLoading = false;
  private treemapFetched = false;
  private treemapToggleSub?: Subscription;

  /**
   * query parameters refresh
   */
  previousValue: any;

  /**
   * columns array
   */
  array_filter = ['Score Events'];

  /**
   * when there is no data for a particular mosaic
   */
  nodata = {};

  /**
   * loading mosaic
   */
  loading = {};

  /**
   * when a particular mosaic is ready
   */
  iready = {};

  /**
   * project based on config file
   */
  project: any;

  /**
   * is one of the scores focused
   */
  scoreOverride: boolean;

  customScore: string;

  /**
   * sorting events — shared across the flag/score/alert mosaics
   */
  eventsSortedBy = 'importance';

  /**
   * screenSize$ observable from breakpointService
   */
  screenSize$ = this.breakpointService.screenSize$;

  private destroy$ = new Subject<void>();
  private qparamSub?: Subscription;
  // The entity/price fetch for the current refresh. Unsubscribed at the start of
  // the next refresh so a slow response for the old entity can't overwrite the
  // new one (stale-data race on rapid entity-type changes).
  private entityPriceSub?: Subscription;

  /**
   * query parameters subscribe and get all the data: treemap, entity, etc...
   * (the mosaic fetches live in the sub-components)
   */
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private compositeData: CompositeDataService,
    public routing: RoutingService,
    public auth: AuthService,
    public featureFlagsService: FeatureFlagsService,
    public userState: UserStateService,
    public config: ConfigService,
    private breakpointService: BreakpointService,
    private sentimentService: SentimentService,
    private priceDataService: PriceDataService,
    private portfolioApi: PortfolioApiService,
    private userStorage: UserScopedStorageService,
  ) {
    this.customScore = this.featureFlagsService.scorelabSettings.defaultScore;

    this.project = this.config.appConfig.routing.project;

    this.qparamSub = this.route.queryParams.subscribe((qparam) => {
      this.qparam = JSON.parse(JSON.stringify(qparam));

      this.customScore = this.featureFlagsService.scorelabSettings.defaultScore;

      // Peergroup/Entity only apply to a single entity (company/pne/industry/
      // sector/isin) — not to a portfolio aggregate, query or search. Set the
      // menu options explicitly per context (don't let a stale value persist).
      const isSingleEntity =
        (qparam.type &&
          (qparam.type === 'company' ||
            qparam.type === 'pne' ||
            ['industry', 'supersector', 'sector', 'subsector'].includes(
              qparam.type,
            ))) ||
        !!qparam.isin;
      const isCountry = qparam.type === 'country';
      this.array_filter = isCountry
        ? ['Score Events', 'Peergroup Comparison']
        : isSingleEntity
          ? ['Score Events', 'Peergroup Comparison', 'Entity']
          : ['Score Events'];

      this.scoreOverride =
        routing.isChart() &&
        (routing.isFolio() || routing.isMarket()) &&
        qparam?.score;

      if (this.scoreOverride) {
        // scroll the chosen event to the top of the events mosaic
        if (qparam?.scoreevent && qparam?.eventid) {
          const isESG = qparam.score === 'esg' ? 'esg' : '';
          const targetId = qparam.eventid + '-' + isESG;
          setTimeout(() => {
            const targetElement = document.getElementById(targetId);
            scrollIntoViewIfNotVisible(targetElement);
          }, 500);
        }
      }

      if (
        this.routing.reFresh(this.qparam, this.previousValue, [
          'update',
          'updatePayload',
          'type',
          'id',
          'isin',
          'time',
          'newsday',
          'lang',
          'feed',
          'categories',
          'continents',
          'countries',
          'factuality',
          'temporality',
          'ranks',
          'panels',
          'period',
          'score_type',
          'score',
        ])
      ) {
        // Cancel the previous refresh's in-flight entity/price fetch so a slow
        // response for the old entity can't overwrite the new one.
        this.entityPriceSub?.unsubscribe();

        this.array_filter.forEach((item) => {
          this.nodata[item] = false;
          this.loading[item] = true;
          this.iready[item] = false;
        });

        this.peergroupCtx = null;
        this.entity = {
          currency: '',
          logo: '',
          price: '',
          price_change: '',
          price_change_diff: '',
        };

        if (
          !(this.customScore === 'sentiment') &&
          (this.routing.isFolio() ||
            (qparam.type &&
              (qparam.type === 'company' ||
                qparam.type === 'pne' ||
                ['industry', 'supersector', 'sector', 'subsector'].includes(
                  qparam.type,
                ))) ||
            qparam.isin)
        ) {
          if (
            !(this.routing.isFolio() && !this.qparam.portfolioId) &&
            !this.routing.isQuery() &&
            !this.routing.isSearch() &&
            !(this.qparam.type === 'pne') &&
            !(this.qparam.type === 'hierarchy')
          ) {
            this.ifilter = new UntypedFormControl([
              'Score Events',
              'Peergroup Comparison',
            ]);
            this.applyStoredFilter('score_company_filter');
          } else {
            this.ifilter = new UntypedFormControl(['Score Events']);
            this.applyStoredFilter('score_mosaics_filter');
          }
        } else {
          if (
            !(this.routing.isFolio() && !this.qparam.portfolioId) &&
            !this.routing.isQuery() &&
            !this.routing.isSearch() &&
            !(this.qparam.type === 'pne') &&
            !(this.qparam.type === 'hierarchy')
          ) {
            this.ifilter = new UntypedFormControl([
              'Score Events',
              'Peergroup Comparison',
            ]);
            this.applyStoredFilter('company_filter');
          } else {
            this.ifilter = new UntypedFormControl(['Score Events']);
            this.applyStoredFilter('mosaics_filter');
          }
        }

        if (
          this.qparam.mosaic === 'scoreevents' ||
          this.qparam.mosaic === 'events'
        ) {
          this.ifilter = new UntypedFormControl(['Score Events']);
        }
        if (this.qparam.mosaic === 'peergroup') {
          this.ifilter = new UntypedFormControl(['Peergroup Comparison']);
        }
        if (this.qparam.mosaic === 'entity') {
          this.ifilter = new UntypedFormControl(['Entity']);
        }

        // Migrate any persisted/deep-linked legacy 'Events' toggle → 'Score Events'.
        this.ifilter.setValue(this.sanitizeMosaicFilter(this.ifilter.value));

        // Drop any selected mosaic that isn't an option for this entity type, so
        // it can't stay visible-but-empty after a type change.
        this.pruneIfilterToOptions();

        // MOSAIC SUB-COMPONENTS (flag/sentiment/score/alert events) ---------

        this.mosaicParams = this.qparam;

        this.watchEntityTreemapToggle();

        // ENTITY + PRICE + PEERGROUP TRIGGER ---------------------------------

        this.entityPriceSub = forkJoin({
          entity: this.sentimentService.getSentiment(this.qparam, false),
          price:
            this.qparam.type && this.qparam.type !== 'country'
              ? this.priceDataService.getYlPrice(
                  this.qparam.type,
                  this.qparam.id,
                  this.qparam,
                )
              : of({ prices: [] }),
        }).subscribe(
          (result) => {
            // ENTITY ---------------------------------------------

            if (
              !(this.routing.isFolio() && !this.qparam.portfolioId) &&
              !(this.qparam.type === 'hierarchy')
            ) {
              if (result.entity.entity.media) {
                if (result.entity.entity.media.uni) {
                  this.entity.logo = result.entity.entity.media.uni;
                }
              }
              this.entity.alpha_id = result.entity.entity.alpha_id;
              this.entity.compound_key = result.entity.entity.compound_key;
              this.entity.name = result.entity.entity.name;
              this.entity.sentiment = result.entity.sentiment.sentiment;
              this.entity.sentiment_delta =
                result.entity.sentiment.sentiment_delta;
            }

            // PRICE ---------------------------------------------

            if (
              !(this.routing.isFolio() && !this.qparam.portfolioId) &&
              !(this.qparam.type === 'hierarchy')
            ) {
              if (result.price['prices'].length > 1) {
                const price1 =
                  result.price['prices'][result.price['prices'].length - 1]
                    .price;
                const price2 =
                  result.price['prices'][result.price['prices'].length - 2]
                    .price;
                this.entity.currency = result.price['currency'];
                this.entity.price = price1;
                this.entity.price_change = this.iNumber(
                  (price1 - price2).toFixed(2),
                );
                this.entity.price_change_diff = this.iPercent(
                  (((price1 - price2) / price2) * 100).toFixed(2),
                );
              }
            }

            this.iready['Entity'] = true;

            // PEERGROUP (delegated to the mosaic component) -------

            this.peergroupCtx = {
              qparam: this.qparam,
              entity: this.entity,
            };
          },
          () => {
            // Reassign (not mutate) the maps: the OnPush peergroup child only
            // re-renders when its `nodata`/`loading` input references change.
            this.nodata = {
              ...this.nodata,
              'Peergroup Comparison': true,
              Entity: true,
            };
            this.loading = {
              ...this.loading,
              'Peergroup Comparison': false,
              Entity: false,
            };
          },
        );
      }
      this.previousValue = this.qparam;
    });
  }

  ngOnDestroy(): void {
    this.treemapToggleSub?.unsubscribe();
    this.qparamSub?.unsubscribe();
    this.entityPriceSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Restore a stored comma-joined filter selection into `ifilter`, if any. */
  private applyStoredFilter(key: string): void {
    const stored = this.userStorage.getItem(key);
    if (stored != null) {
      this.ifilter.setValue(stored.split(','));
    }
  }

  // Fires `compositeData.treemap()` (7-way `/related/` + sentiment fan-out) only
  // when the user has `'Entity'` selected in `ifilter`. The widget is
  // hidden by default in most filter presets and always hidden when
  // `qparam.score` is set, so the eager fetch was wasted work.
  private watchEntityTreemapToggle(): void {
    this.treemapToggleSub?.unsubscribe();
    this.treemapsort = undefined;
    this.treemapFetched = false;
    this.treemapLoading = false;

    const skip =
      ['industry', 'supersector', 'sector', 'subsector', 'hierarchy'].includes(
        this.qparam?.type,
      ) ||
      (this.routing.isFolio() && !this.qparam.portfolioId) ||
      !this.ifilter;
    if (skip) return;

    this.treemapToggleSub = this.ifilter.valueChanges
      .pipe(
        startWith(this.ifilter.value),
        filter(
          (value: unknown) =>
            Array.isArray(value) &&
            value.includes('Entity') &&
            !this.qparam?.score,
        ),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.fetchTreemap());
  }

  private fetchTreemap(): void {
    if (this.treemapFetched) return;
    this.treemapFetched = true;
    this.treemapLoading = true;
    this.compositeData
      .treemap(this.qparam)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          const treemap: any[] = [];
          result?.treemap.children.forEach((item: any) => {
            item?.children?.forEach((itom: any) => {
              treemap.push(itom);
            });
          });
          this.treemapsort = treemap.sort(
            (a, b) => a.entity.sentiment_delta - b.entity.sentiment_delta,
          );
          this.treemapLoading = false;
        },
        error: () => {
          this.treemapLoading = false;
        },
      });
  }

  /**
   *
   */
  iNumber(value) {
    const icon = value > 0 ? '+' : '';
    return icon + value;
  }

  /**
   *
   */
  iPercent(value) {
    const icon = value > 0 ? '+' : '';
    return icon + value + '%';
  }

  /**
   *
   */
  iAbs(value) {
    return Math.abs(value * 100).toFixed(0);
  }

  /**
   * redirect specific entity
   */
  iEntity(type, id) {
    this.router.navigate([], {
      queryParams: {
        type: type,
        id: id,
        tag: null,
        news: null,
        eventid: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * changelter the visualization columns
   */
  iFilter(event) {
    if (event === false) {
      if (
        !(this.customScore === 'sentiment') &&
        (this.routing.isFolio() ||
          (this.qparam.type &&
            (this.qparam.type === 'company' ||
              this.qparam.type === 'pne' ||
              ['industry', 'supersector', 'sector', 'subsector'].includes(
                this.qparam.type,
              ))) ||
          this.qparam.isin)
      ) {
        if (
          !(this.routing.isFolio() && !this.qparam.portfolioId) &&
          !(this.qparam.type === 'hierarchy')
        ) {
          this.userStorage.setItem('score_company_filter', this.ifilter.value);
        } else {
          this.userStorage.setItem('score_mosaics_filter', this.ifilter.value);
        }
      } else {
        if (
          !(this.routing.isFolio() && !this.qparam.portfolioId) &&
          !(this.qparam.type === 'hierarchy')
        ) {
          this.userStorage.setItem('company_filter', this.ifilter.value);
        } else {
          this.userStorage.setItem('mosaics_filter', this.ifilter.value);
        }
      }
    }
  }

  /**
   * Effective score driving the events mosaic: the URL `score` override when
   * present, otherwise the user's default score. `'sentiment'` → Sentiment
   * Events mosaic; anything else → Score Events mosaic.
   */
  get effectiveScore(): string {
    if (
      this.routing.isQuery() ||
      this.routing.isSearch() ||
      this.qparam?.type === 'country'
    ) {
      return 'sentiment';
    }
    return this.qparam?.score || this.customScore;
  }

  /**
   * Maps the legacy `'Events'` toggle to the unified `'Score Events'` toggle
   * (and dedupes), so persisted/deep-linked filters keep working after the
   * `'Events'` option was removed.
   */
  private sanitizeMosaicFilter(values: string[]): string[] {
    return [
      ...new Set(
        (values || []).map((v) => (v === 'Events' ? 'Score Events' : v)),
      ),
    ];
  }

  /**
   *
   */
  iTool(value) {
    if (value === 'Score Events' && this.qparam.score) {
      return true;
    }
    return this.ifilter.value.includes(value);
  }

  /**
   * True when Score Events is the only open mosaic (no Peergroup/Entity beside
   * it). The score mosaic shows its tiny temporality/event graphs only then —
   * with another mosaic open we hide them to save horizontal space.
   */
  scoreEventsSole(): boolean {
    const peergroupOpen =
      this.iTool('Peergroup Comparison') && !this.qparam?.score;
    const entityOpen = this.iTool('Entity') && !this.qparam?.score;
    return !peergroupOpen && !entityOpen;
  }

  /** Peergroup mosaic narrows the column list when no peergroup exists. */
  onArrayFilterChange(filterList: string[]): void {
    this.array_filter = filterList;
    this.pruneIfilterToOptions();
  }

  /**
   * Keep the selected mosaics (`ifilter`, which drives `iTool()` → visibility) a
   * subset of the available menu options (`array_filter`). When a type change or
   * the peergroup narrowing drops an option, the matching mosaic stops being
   * visible instead of lingering empty. `emitEvent: false` keeps this invisible
   * to `watchEntityTreemapToggle`'s `valueChanges`; the no-op guard prevents a
   * redundant write on every refresh.
   */
  private pruneIfilterToOptions(): void {
    if (!this.ifilter) return;
    const current: string[] = this.ifilter.value || [];
    const pruned = current.filter((v) => this.array_filter.includes(v));
    if (pruned.length === current.length) return;
    this.ifilter.setValue(pruned, { emitEvent: false });
  }
}
